'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { getCurrentUser } from '@/app/apis';
import { BaseLineMinus } from '@/app/components/icons/BaseLineMinus';
import { BaseLinePlus } from '@/app/components/icons/BaseLinePlus';
import { Loading } from '@/app/components/icons/Loading';
import { ImageWithPlaceholder as ImageModel } from '@/app/components/shared/ImageWithPlaceholder';
import { Button } from '@/app/components/ui/button';
import { Dialog, DialogContent } from '@/app/components/ui/dialog';
import { Slider } from '@/app/components/ui/slider';
import { toast } from '@/app/components/ui/use-toast';
import { UPLOAD_SCENE } from '@/app/constants';
import { useFetch } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import { formatFileSize, getImageSize } from '@/app/utils';
import { handleServiceError, uploadFile } from '@/app/utils/client';
import { usersUpdateProfile } from './apis';
import { CONTAINER_SIZE, MAX_SCALE, MIN_SCALE, STEP } from './constants';
import type { UploadFileInfo } from './types';

const MAX_IMAGE_FILE_SIZE = 1 * 1024 * 1024;

interface UserAvatarDialogProps {
  confirmUrl: string;
  onClose: () => void;
  open: boolean;
  setUrl?: (url: string) => void;
  fileUploadInfo?: UploadFileInfo;
}

export const UserAvatarDialog: React.FC<UserAvatarDialogProps> = ({ open, onClose, confirmUrl, setUrl }) => {
  const t = useTranslations();
  const [scale, setScale] = useState(100);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [imageUrl, setImageUrl] = useState('');
  const { updateUserInfo } = useStore((state) => ({
    updateUserInfo: state.updateUserInfo,
  }));

  // 添加 useEffect 以监听 confirmUrl 的变化
  useEffect(() => {
    // 重置所有状态到初始值
    setScale(100);
    setPosition({ x: 0, y: 0 });
    setIsDragging(false);
    setDragStart({ x: 0, y: 0 });
    setImageSize({ width: 0, height: 0 });
  }, [confirmUrl]);

  // 图片加载完成后获取实际尺寸
  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.target as HTMLImageElement;

    setImageSize({
      width: img.naturalWidth,
      height: img.naturalHeight,
    });
  };

  // 修改计算图片展示尺寸的方法
  const calculateImageDisplaySize = () => {
    if (!imageSize.width || !imageSize.height) return { width: '100%', height: '100%' };

    const scale = Math.max(CONTAINER_SIZE / imageSize.width, CONTAINER_SIZE / imageSize.height);

    return {
      width: `${imageSize.width * scale}px`,
      height: `${imageSize.height * scale}px`,
    };
  };

  // 修改可拖拽范围的计算
  const calculateDragBounds = (newX: number, newY: number) => {
    const displaySize = calculateImageDisplaySize();
    const currentWidth = Number.parseFloat(displaySize.width) * (scale / 100);
    const currentHeight = Number.parseFloat(displaySize.height) * (scale / 100);

    const maxX = (currentWidth - CONTAINER_SIZE) / 2;
    const maxY = (currentHeight - CONTAINER_SIZE) / 2;

    return {
      x: Math.min(Math.max(newX, -maxX), maxX),
      y: Math.min(Math.max(newY, -maxY), maxY),
    };
  };

  const { run: getUserInfo } = useFetch(getCurrentUser, {
    manual: true,
    suppressUnauthorizedLoginDialog: true,
    onSuccess: ({ data }) => {
      if (data) {
        // 更新 store 中的用户信息
        updateUserInfo(data);
      }
    },
  });

  const { loading: updateAvatarLoading, run: updateAvatar } = useFetch(
    (uri: string) =>
      usersUpdateProfile({
        body: {
          profile: {
            avatar: uri,
          },
          mask: 'avatar',
        },
      }),
    {
      manual: true,
      onSuccess: ({ data, error }) => {
        if (error) {
          if (error.reason === 'AlreadySubmittedAudited') {
            toast({
              type: 'warning',
              message: t('personal.bio.auditing'),
            });
            return;
          }
          handleServiceError(error, t);
          return;
        }
        if (data) {
          toast({
            title: t('personal.avatarDialog.success'),
          });
          getUserInfo();
          onClose();
        }
        if (setUrl) {
          setUrl(imageUrl);
        }
      },
      onError: (error) => {
        toast({
          title: error.message,
        });
      },
    },
  );

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;

    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;

    // 应用边界限制
    const boundedPosition = calculateDragBounds(newX, newY);
    setPosition(boundedPosition);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleScaleChange = (value: number[]) => {
    setScale(value[0]);
  };

  const handleDecrease = () => {
    setScale((prev) => Math.max(prev - STEP, MIN_SCALE));
  };

  const handleIncrease = () => {
    setScale((prev) => Math.min(prev + STEP, MAX_SCALE));
  };

  const handleClose = () => {
    //TODO:
    onClose();
  };

  // 添加裁剪函数
  const cropImage = async () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    canvas.width = CONTAINER_SIZE;
    canvas.height = CONTAINER_SIZE;

    const img = new Image();
    img.crossOrigin = 'anonymous';

    return new Promise<File | null>((resolve) => {
      img.onload = () => {
        // 计算实际显示尺寸和缩放比例
        const displaySize = calculateImageDisplaySize();
        const displayWidth = Number.parseFloat(displaySize.width);
        const displayHeight = Number.parseFloat(displaySize.height);

        // 计算原始图片相对于显示尺寸的缩放比例
        const imageScaleX = img.naturalWidth / displayWidth;
        const imageScaleY = img.naturalHeight / displayHeight;

        // 计算缩放后的尺寸
        const scaledWidth = displayWidth * (scale / 100);
        const scaledHeight = displayHeight * (scale / 100);

        // 计算在原始图片上的裁剪位置
        const centerOffsetX = (scaledWidth - CONTAINER_SIZE) / 2;
        const centerOffsetY = (scaledHeight - CONTAINER_SIZE) / 2;

        const sourceX = ((centerOffsetX - position.x) * imageScaleX) / (scale / 100);
        const sourceY = ((centerOffsetY - position.y) * imageScaleY) / (scale / 100);
        const sourceSize = (CONTAINER_SIZE * imageScaleX) / (scale / 100);

        // 绘制圆形裁剪区域
        ctx.beginPath();
        ctx.arc(CONTAINER_SIZE / 2, CONTAINER_SIZE / 2, CONTAINER_SIZE / 2, 0, Math.PI * 2);
        ctx.clip();

        // 从原始图片中裁剪对应区域
        ctx.drawImage(img, sourceX, sourceY, sourceSize, sourceSize, 0, 0, CONTAINER_SIZE, CONTAINER_SIZE);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              // 将 Blob 转换为 File，添加文件名和类型
              const file = new File([blob], 'avatar.webp', { type: 'image/webp' });
              resolve(file);
            } else {
              resolve(null);
            }
          },
          'image/webp',
          0.9,
        );
      };

      img.onerror = () => resolve(null);
      img.src = confirmUrl;
    });
  };

  const handleConfirm = async () => {
    const croppedImage = await cropImage();

    if (croppedImage) {
      // 检查文件大小（不能超过1MB）
      if (croppedImage.size > MAX_IMAGE_FILE_SIZE) {
        toast({
          title: t('create.createForm.upload.error.invalidSize', { limit: formatFileSize(MAX_IMAGE_FILE_SIZE) ?? '' }),
        });
        return;
      }
      const croppedImageUrl = URL.createObjectURL(croppedImage);
      setImageUrl(croppedImageUrl);
      const { width, height } = await getImageSize(croppedImageUrl);
      const sourceImageResult = await uploadFile({ file: croppedImage, width, height, scene: UPLOAD_SCENE.AVATAR });
      if (sourceImageResult.error?.reason === 'AuditSubmitIllegal') {
        toast({ type: 'error', title: t('create.createForm.upload.error.AuditSubmitIllegal') });
        return;
      }
      await updateAvatar(sourceImageResult.uri);
    }
  };

  const handleCancel = () => {
    if (setUrl) {
      setUrl('');
    }
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="w-[360px]" onClose={handleCancel} disableOverlayClose={true}>
        <div className="font-medium text-system-text01 text-xl">{t('personal.avatarDialog.edit')}</div>
        <div className="flex justify-center">
          <div className="relative size-[256px] overflow-hidden rounded-full" style={{ touchAction: 'none' }}>
            <ImageModel
              src={confirmUrl}
              alt="avatar"
              className="absolute top-1/2 left-1/2 cursor-move"
              style={{
                ...calculateImageDisplaySize(),
                transform: `translate(-50%, -50%) translate(${position.x}px, ${position.y}px) scale(${scale / 100})`,
                transformOrigin: 'center',
                maxWidth: 'none',
                userSelect: 'none',
                pointerEvents: 'auto',
              }}
              onLoad={handleImageLoad}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchStart={(e) => {
                e.preventDefault();
                const touch = e.touches[0];
                handleMouseDown({ clientX: touch.clientX, clientY: touch.clientY } as React.MouseEvent);
              }}
              onTouchMove={(e) => {
                e.preventDefault();
                const touch = e.touches[0];
                handleMouseMove({ clientX: touch.clientX, clientY: touch.clientY } as React.MouseEvent);
              }}
              onTouchEnd={() => handleMouseUp()}
              draggable={false}
            />
          </div>
        </div>
        <div className="flex items-center gap-4 px-4 py-2">
          <Button size="icon" className="size-4 shrink-0" onClick={handleDecrease} disabled={scale <= MIN_SCALE}>
            <BaseLineMinus className="size-4" />
          </Button>
          <Slider
            value={[scale]}
            min={MIN_SCALE}
            max={MAX_SCALE}
            step={STEP}
            onValueChange={handleScaleChange}
            className="flex-1"
          />
          <Button size="icon" className="size-4 shrink-0" onClick={handleIncrease} disabled={scale >= MAX_SCALE}>
            <BaseLinePlus className="size-4" />
          </Button>
        </div>
        <div className="pointer-events-none flex justify-between gap-4 sm:w-full md:mt-0">
          <Button
            onClick={handleCancel}
            variant="outline"
            className="pointer-events-auto h-9 px-[49px] py-[6px] text-sm"
          >
            {t('create.createForm.upload.cancel')}
          </Button>
          <Button
            disabled={updateAvatarLoading}
            onClick={handleConfirm}
            variant="primary"
            className="pointer-events-auto h-9 px-[49px] py-[6px] text-sm"
          >
            {updateAvatarLoading ? (
              <Loading className="mr-2 animate-spin text-base" />
            ) : (
              t('create.createForm.upload.confirm')
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
